import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Bell, Users, MousePointerClick, DollarSign, Loader2, Globe,
    Send, Clock, Repeat, ArrowUpRight, ArrowDownRight, Zap, BarChart3,
    Eye, X, TrendingUp, Megaphone,
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { CompactDateRangePicker } from '@/pages/Dashboard/components/CompactDateRangePicker';
import {
    getPushDomains, getPushLeadStats,
    getPushDashboardOverview, getPushDashboardCampaigns, getPushDashboardFlows,
} from '@/services/api/push';
import {
    AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import type {
    DashboardOverview, DashboardCampaignItem, DashboardFlowItem,
    DashboardCampaignsReport, DashboardFlowsReport, PushLeadStats,
} from '@/types/push';

// ─── Constants ───

const CHART_COLORS = {
    indigo: '#6366f1',
    emerald: '#10b981',
    amber: '#f59e0b',
    pink: '#ec4899',
    purple: '#8b5cf6',
    teal: '#14b8a6',
    blue: '#3b82f6',
    orange: '#f97316',
};

const PIE_COLORS = [CHART_COLORS.indigo, CHART_COLORS.emerald];

// ─── Helpers ───

function fmt$(v: number) {
    return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
}

function fmtN(v: number) {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
    return v.toLocaleString();
}

function fmtDate(date: string | null) {
    if (!date) return '-';
    return new Date(date).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit',
    });
}

function getDefaultDates() {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const d = yesterday.toISOString().split('T')[0];
    return { start: d, end: d };
}

// ─── Custom Chart Tooltip ───

function ChartTooltipContent({ active, payload, label }: any) {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-xl border border-gray-200 bg-white/95 px-4 py-3 shadow-xl backdrop-blur-sm">
            <p className="mb-2 text-xs font-medium text-gray-500">{label}</p>
            {payload.map((p: any, i: number) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                    <div className="h-2.5 w-2.5 rounded-full" style={{ background: p.color }} />
                    <span className="text-gray-600">{p.name}:</span>
                    <span className="font-semibold text-gray-900">
                        {p.name.toLowerCase().includes('receita') || p.name.toLowerCase().includes('revenue')
                            ? fmt$(p.value)
                            : fmtN(p.value)}
                    </span>
                </div>
            ))}
        </div>
    );
}

// ─── Metric Card (gradient blob style) ───

function MetricCard({ label, value, sub, color, trend }: {
    label: string; value: string | number; sub?: string;
    color: 'indigo' | 'emerald' | 'pink' | 'amber' | 'blue' | 'purple' | 'teal';
    trend?: number;
}) {
    const colorMap: Record<string, { bg: string; blob: string; text: string }> = {
        indigo: { bg: 'from-indigo-500/5 to-indigo-500/0', blob: 'bg-indigo-500/10', text: 'text-indigo-600' },
        emerald: { bg: 'from-emerald-500/5 to-emerald-500/0', blob: 'bg-emerald-500/10', text: 'text-emerald-600' },
        pink: { bg: 'from-pink-500/5 to-pink-500/0', blob: 'bg-pink-500/10', text: 'text-pink-600' },
        amber: { bg: 'from-amber-500/5 to-amber-500/0', blob: 'bg-amber-500/10', text: 'text-amber-600' },
        blue: { bg: 'from-blue-500/5 to-blue-500/0', blob: 'bg-blue-500/10', text: 'text-blue-600' },
        purple: { bg: 'from-purple-500/5 to-purple-500/0', blob: 'bg-purple-500/10', text: 'text-purple-600' },
        teal: { bg: 'from-teal-500/5 to-teal-500/0', blob: 'bg-teal-500/10', text: 'text-teal-600' },
    };
    const c = colorMap[color];

    return (
        <div className={`group relative overflow-hidden rounded-2xl border border-gray-100 bg-gradient-to-br ${c.bg} p-5 transition-all hover:shadow-lg hover:shadow-gray-200/50`}>
            {/* Decorative blob */}
            <div className={`absolute -right-4 -top-4 h-20 w-20 rounded-full ${c.blob} blur-2xl transition-transform group-hover:scale-150`} />

            <p className="text-xs font-medium uppercase tracking-wider text-gray-400">{label}</p>
            <p className={`mt-1.5 text-2xl font-bold ${c.text}`}>{value}</p>
            <div className="mt-1 flex items-center gap-2">
                {sub && <p className="text-xs text-gray-400">{sub}</p>}
                {trend !== undefined && trend !== 0 && (
                    <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-semibold ${
                        trend > 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'
                    }`}>
                        {trend > 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                        {Math.abs(trend)}%
                    </span>
                )}
            </div>
        </div>
    );
}

// ─── Hero Summary Cards (large gradient) ───

function HeroCard({ label, value, sub, gradient, icon: Icon }: {
    label: string; value: string; sub?: string;
    gradient: string; icon: any;
}) {
    return (
        <div className={`relative overflow-hidden rounded-2xl ${gradient} p-6 text-white shadow-lg`}>
            <div className="absolute -right-6 -top-6 opacity-10">
                <Icon className="h-28 w-28" strokeWidth={1} />
            </div>
            <p className="text-sm font-medium text-white/70">{label}</p>
            <p className="mt-2 text-3xl font-bold">{value}</p>
            {sub && <p className="mt-1 text-sm text-white/60">{sub}</p>}
        </div>
    );
}

// ─── Push Notification Preview ───

function PushPreview({ title, body, icon, url }: {
    title: string; body: string; icon?: string | null; url?: string;
}) {
    return (
        <div className="flex items-start gap-3 rounded-xl border border-gray-100 bg-gray-50/80 p-3">
            {icon ? (
                <img src={icon} alt="" className="h-10 w-10 rounded-lg object-cover" />
            ) : (
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600">
                    <Bell className="h-5 w-5 text-white" />
                </div>
            )}
            <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 line-clamp-1">{title || 'Sem título'}</p>
                <p className="mt-0.5 text-xs text-gray-500 line-clamp-2">{body || 'Sem conteúdo'}</p>
                {url && (
                    <p className="mt-1 text-[10px] text-gray-300 truncate">{url}</p>
                )}
            </div>
        </div>
    );
}

// ─── Status Badge ───

function StatusBadge({ status }: { status: string }) {
    const config: Record<string, { label: string; cls: string }> = {
        sent: { label: 'Enviada', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-600/10' },
        active: { label: 'Ativo', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-600/10' },
        draft: { label: 'Rascunho', cls: 'bg-gray-50 text-gray-600 ring-gray-500/10' },
        scheduled: { label: 'Agendada', cls: 'bg-blue-50 text-blue-700 ring-blue-600/10' },
        sending: { label: 'Enviando', cls: 'bg-amber-50 text-amber-700 ring-amber-600/10' },
        paused: { label: 'Pausado', cls: 'bg-amber-50 text-amber-700 ring-amber-600/10' },
        cancelled: { label: 'Cancelada', cls: 'bg-red-50 text-red-600 ring-red-500/10' },
        failed: { label: 'Falhou', cls: 'bg-red-50 text-red-700 ring-red-600/10' },
    };
    const c = config[status] || config.draft;
    return (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${c.cls}`}>
            {c.label}
        </span>
    );
}

// ─── Type Badge ───

function TypeBadge({ type }: { type: string }) {
    const icons: Record<string, any> = { direct: Send, scheduled: Clock, recurring: Repeat };
    const labels: Record<string, string> = { direct: 'Direta', scheduled: 'Agendada', recurring: 'Recorrente' };
    const Icon = icons[type] || Send;
    return (
        <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-600 ring-1 ring-inset ring-indigo-500/10">
            <Icon className="h-3 w-3" />
            {labels[type] || type}
        </span>
    );
}

// ─── CTR Badge ───

function CtrBadge({ ctr }: { ctr: number }) {
    const cls = ctr >= 5
        ? 'bg-emerald-50 text-emerald-700'
        : ctr >= 2
            ? 'bg-amber-50 text-amber-700'
            : 'bg-gray-50 text-gray-600';
    return (
        <span className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-bold ${cls}`}>
            {ctr >= 5 && <ArrowUpRight className="h-3 w-3" />}
            {ctr < 2 && ctr > 0 && <ArrowDownRight className="h-3 w-3" />}
            {ctr}%
        </span>
    );
}

// ─── Revenue Area Chart ───

function RevenueChart({ data }: { data: { date: string; revenue: number; impressions: number; clicks: number }[] }) {
    if (!data.length) return <EmptyState text="Sem dados de receita no período" />;

    const formatted = data.map((d) => ({
        ...d,
        date: d.date.slice(5),
        revenue: Number(Number(d.revenue).toFixed(2)),
    }));

    return (
        <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={formatted}>
                <defs>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={CHART_COLORS.indigo} stopOpacity={0.25} />
                        <stop offset="100%" stopColor={CHART_COLORS.indigo} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="clickGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={CHART_COLORS.pink} stopOpacity={0.2} />
                        <stop offset="100%" stopColor={CHART_COLORS.pink} stopOpacity={0} />
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                <YAxis yAxisId="rev" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
                <YAxis yAxisId="clicks" orientation="right" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                <Tooltip content={<ChartTooltipContent />} />
                <Legend verticalAlign="top" height={36} />
                <Area yAxisId="rev" type="monotone" dataKey="revenue" name="Receita" stroke={CHART_COLORS.indigo} strokeWidth={2.5} fill="url(#revGrad)" />
                <Area yAxisId="clicks" type="monotone" dataKey="clicks" name="Clicks" stroke={CHART_COLORS.pink} strokeWidth={2} fill="url(#clickGrad)" />
            </AreaChart>
        </ResponsiveContainer>
    );
}

// ─── Campaign Revenue Bar Chart ───

function TopCampaignsBars({ items }: { items: DashboardCampaignItem[] }) {
    if (!items.length) return <EmptyState text="Nenhuma campanha no período" />;

    const top = [...items]
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 8)
        .map((c) => ({
            name: c.name.length > 20 ? c.name.slice(0, 20) + '…' : c.name,
            Receita: Number(c.revenue.toFixed(2)),
            Clicks: c.total_clicks,
        }));

    return (
        <ResponsiveContainer width="100%" height={300}>
            <BarChart data={top} layout="vertical" margin={{ left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={(v) => `$${v}`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} width={140} />
                <Tooltip content={<ChartTooltipContent />} />
                <Bar dataKey="Receita" fill={CHART_COLORS.indigo} radius={[0, 6, 6, 0]} barSize={18} />
            </BarChart>
        </ResponsiveContainer>
    );
}

// ─── Revenue Split Donut ───

function RevenueSplitDonut({ campaigns, flows }: { campaigns: number; flows: number }) {
    const total = campaigns + flows;
    if (total === 0) return <EmptyState text="Sem receita no período" />;

    const data = [
        { name: 'Campanhas', value: Number(campaigns.toFixed(2)) },
        { name: 'Flows', value: Number(flows.toFixed(2)) },
    ];

    return (
        <div className="flex flex-col items-center">
            <ResponsiveContainer width={200} height={200}>
                <PieChart>
                    <Pie
                        data={data}
                        innerRadius={55}
                        outerRadius={85}
                        dataKey="value"
                        stroke="none"
                        paddingAngle={4}
                    >
                        {data.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmt$(Number(v))} contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0' }} />
                </PieChart>
            </ResponsiveContainer>
            <div className="mt-2 flex items-center gap-6">
                {data.map((d, i) => (
                    <div key={d.name} className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full" style={{ background: PIE_COLORS[i] }} />
                        <div>
                            <p className="text-xs text-gray-500">{d.name}</p>
                            <p className="text-sm font-bold text-gray-900">{fmt$(d.value)}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ─── Empty State ───

function EmptyState({ text }: { text: string }) {
    return (
        <div className="flex h-48 flex-col items-center justify-center text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-50">
                <BarChart3 className="h-6 w-6 text-gray-300" />
            </div>
            <p className="mt-3 text-sm text-gray-400">{text}</p>
        </div>
    );
}

// ─── Section Card wrapper ───

function Section({ children, className = '' }: { children: React.ReactNode; className?: string }) {
    return (
        <div className={`overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm ${className}`}>
            {children}
        </div>
    );
}

function SectionHeader({ title, right }: { title: string; right?: React.ReactNode }) {
    return (
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
            <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
            {right}
        </div>
    );
}

// ════════════════════════════════════════════
//  MAIN DASHBOARD
// ════════════════════════════════════════════

export default function Dashboard() {
    const defaults = getDefaultDates();
    const [startDate, setStartDate] = useState(defaults.start);
    const [endDate, setEndDate] = useState(defaults.end);
    const [selectedDomainId, setSelectedDomainId] = useState<string>('all');
    const [activeTab, setActiveTab] = useState<'overview' | 'campaigns' | 'flows'>('overview');

    const { data: domains = [] } = useQuery({ queryKey: ['push-domains'], queryFn: getPushDomains });
    const configuredDomains = domains.filter((d) => d.push_config?.mongo_domain_id);

    const isAllDomains = selectedDomainId === 'all';
    const parsedDomainId = isAllDomains ? undefined : parseInt(selectedDomainId);

    // Single request per endpoint (backend handles aggregation when domain_id is undefined)
    const { data: overviewData, isLoading: loadingOverview } = useQuery({
        queryKey: ['push-dashboard-overview', parsedDomainId, startDate, endDate],
        queryFn: () => getPushDashboardOverview({
            domain_id: parsedDomainId as number,
            start: startDate,
            end: endDate,
        }),
        enabled: isAllDomains || !!parsedDomainId,
    });

    const { data: campaignsReportData, isLoading: loadingCampaigns } = useQuery({
        queryKey: ['push-dashboard-campaigns', parsedDomainId, startDate, endDate],
        queryFn: () => getPushDashboardCampaigns({
            domain_id: parsedDomainId as number,
            start: startDate,
            end: endDate,
        }),
        enabled: isAllDomains || !!parsedDomainId,
    });

    const { data: flowsReportData, isLoading: loadingFlows } = useQuery({
        queryKey: ['push-dashboard-flows', parsedDomainId, startDate, endDate],
        queryFn: () => getPushDashboardFlows({
            domain_id: parsedDomainId as number,
            start: startDate,
            end: endDate,
        }),
        enabled: isAllDomains || !!parsedDomainId,
    });

    const { data: leadStatsData, isLoading: loadingLeadStats } = useQuery({
        queryKey: ['push-lead-stats', parsedDomainId],
        queryFn: () => getPushLeadStats(parsedDomainId),
        enabled: isAllDomains || !!parsedDomainId,
    });

    const overview = useMemo(() => overviewData, [overviewData]
    );
    const campaignsReport = useMemo(() => campaignsReportData, [campaignsReportData]);
    const flowsReport = useMemo(() => flowsReportData, [flowsReportData]);
    const leadStats = useMemo(() => leadStatsData, [leadStatsData]);

    const isLoading = loadingOverview || loadingLeadStats
        || (activeTab === 'campaigns' && loadingCampaigns)
        || (activeTab === 'flows' && loadingFlows);

    const handleDateChange = (start: string, end: string) => {
        setStartDate(start);
        setEndDate(end);
    };

    return (
        <div className="space-y-6">
            {/* ─── Header ─── */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Dashboard Push</h1>
                    <p className="mt-0.5 text-sm text-gray-500">Métricas de engajamento, conversão e receita</p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    {/* Domain selector */}
                    <Select value={selectedDomainId} onValueChange={setSelectedDomainId}>
                        <SelectTrigger className="w-56 rounded-xl border-gray-200 bg-white shadow-sm">
                            <Globe className="mr-2 h-4 w-4 text-gray-400" />
                            <SelectValue placeholder="Selecione domínio" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos os domínios</SelectItem>
                            {configuredDomains.map((d) => (
                                <SelectItem key={d.id} value={d.id.toString()}>{d.domain}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    {/* Date range picker */}
                    <CompactDateRangePicker
                        startDate={startDate}
                        endDate={endDate}
                        onDateChange={handleDateChange}
                    />
                </div>
            </div>

            {configuredDomains.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50/50 p-16 text-center">
                    <Globe className="mx-auto h-12 w-12 text-gray-300" />
                    <p className="mt-4 text-sm text-gray-500">Configure um domínio primeiro em <strong>Setup</strong></p>
                </div>
            ) : (
                <>
                    {/* ─── Tabs ─── */}
                    <div className="flex gap-1 rounded-xl bg-gray-100/80 p-1">
                        {(['overview', 'campaigns', 'flows'] as const).map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-all ${
                                    activeTab === tab
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                {tab === 'overview' ? 'Visão Geral' : tab === 'campaigns' ? 'Campanhas' : 'Flows'}
                            </button>
                        ))}
                    </div>

                    {isLoading ? (
                        <div className="flex items-center justify-center py-20">
                            <div className="text-center">
                                <Loader2 className="mx-auto h-8 w-8 animate-spin text-indigo-500" />
                                <p className="mt-3 text-sm text-gray-400">Carregando dados...</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            {activeTab === 'overview' && (
                                <OverviewTab
                                    overview={overview}
                                    leadStats={leadStats}
                                    campaignItems={campaignsReport?.items || []}
                                    flowItems={flowsReport?.items || []}
                                />
                            )}
                            {activeTab === 'campaigns' && <CampaignsTab report={campaignsReport} />}
                            {activeTab === 'flows' && <FlowsTab report={flowsReport} />}
                        </>
                    )}
                </>
            )}
        </div>
    );
}

// ════════════════════════════════════════════
//  OVERVIEW TAB
// ════════════════════════════════════════════

function OverviewTab({ overview, leadStats, campaignItems, flowItems }: {
    overview?: DashboardOverview;
    leadStats?: PushLeadStats;
    campaignItems: DashboardCampaignItem[];
    flowItems: DashboardFlowItem[];
}) {
    const cs = overview?.campaigns;
    const fs = overview?.flows;
    const rv = overview?.revenue;

    const [selectedCampaign, setSelectedCampaign] = useState<DashboardCampaignItem | null>(null);
    const [selectedFlow, setSelectedFlow] = useState<DashboardFlowItem | null>(null);

    return (
        <div className="space-y-6">
            {/* Hero cards row */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <HeroCard
                    label="Leads Ativos"
                    value={fmtN(leadStats?.active || 0)}
                    sub={`${fmtN(leadStats?.total || 0)} total · ${fmtN(leadStats?.unsubscribed || 0)} unsub`}
                    gradient="bg-gradient-to-br from-emerald-500 to-teal-600"
                    icon={Users}
                />
                <HeroCard
                    label="Push Enviados"
                    value={fmtN(cs?.total_sent || 0)}
                    sub={`${cs?.total_campaigns || 0} campanhas · ${cs?.total_delivered || 0} entregues`}
                    gradient="bg-gradient-to-br from-indigo-500 to-blue-600"
                    icon={Bell}
                />
                <HeroCard
                    label="Receita Total"
                    value={fmt$(rv?.total_revenue || 0)}
                    sub={`eCPM ${fmt$(rv?.ecpm || 0)} · ${fmtN(rv?.total_impressions || 0)} imp`}
                    gradient="bg-gradient-to-br from-amber-500 to-orange-600"
                    icon={DollarSign}
                />
                <HeroCard
                    label="Total Clicks"
                    value={fmtN((cs?.total_clicks || 0) + (fs?.total_clicks || 0))}
                    sub={`CTR campanhas ${cs?.ctr || 0}% · ${fs?.total_flows || 0} flows ativos`}
                    gradient="bg-gradient-to-br from-pink-500 to-rose-600"
                    icon={MousePointerClick}
                />
            </div>

            {/* Metric cards detail */}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
                <MetricCard label="Campanhas" value={cs?.total_campaigns || 0} color="indigo" sub="no período" />
                <MetricCard label="Entregues" value={fmtN(cs?.total_delivered || 0)} color="blue" sub={`${cs?.total_failed || 0} falhas`} />
                <MetricCard label="CTR Campanhas" value={`${cs?.ctr || 0}%`} color="pink" />
                <MetricCard label="Flows Ativos" value={fs?.total_flows || 0} color="purple" sub={`${fmtN(fs?.total_entered || 0)} leads`} />
                <MetricCard label="Completaram" value={`${fs?.completion_rate || 0}%`} color="teal" sub={`${fmtN(fs?.total_completed || 0)} leads`} />
                <MetricCard label="Impressões" value={fmtN(rv?.total_impressions || 0)} color="amber" />
            </div>

            {/* Charts row */}
            <div className="grid gap-6 lg:grid-cols-3">
                {/* Revenue over time */}
                <Section className="lg:col-span-2">
                    <SectionHeader
                        title="Receita e Clicks Diários"
                        right={<span className="text-xs text-gray-400">Push Notifications</span>}
                    />
                    <div className="p-6">
                        <RevenueChart data={rv?.by_date || []} />
                    </div>
                </Section>

                {/* Revenue split */}
                <Section>
                    <SectionHeader title="Receita por Canal" />
                    <div className="p-6">
                        <RevenueSplitDonut
                            campaigns={rv?.campaigns_revenue || 0}
                            flows={rv?.flows_revenue || 0}
                        />
                        <div className="mt-6 space-y-2.5 border-t border-gray-100 pt-4">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500">Total</span>
                                <span className="text-sm font-bold text-gray-900">{fmt$(rv?.total_revenue || 0)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500">Impressões</span>
                                <span className="text-sm font-semibold text-gray-700">{fmtN(rv?.total_impressions || 0)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-500">eCPM</span>
                                <span className="text-sm font-semibold text-gray-700">{fmt$(rv?.ecpm || 0)}</span>
                            </div>
                        </div>
                    </div>
                </Section>
            </div>

            {/* Top campaigns chart */}
            <Section>
                <SectionHeader
                    title="Top Campanhas por Receita"
                    right={<span className="text-xs text-gray-400">Top 8</span>}
                />
                <div className="p-6">
                    <TopCampaignsBars items={campaignItems} />
                </div>
            </Section>

            {/* Recent campaigns & flows preview */}
            <div className="grid gap-6 lg:grid-cols-2">
                {/* Recent campaigns */}
                <Section>
                    <SectionHeader title="Campanhas Recentes" right={
                        <span className="text-xs text-gray-400">{campaignItems.length} campanhas</span>
                    } />
                    <div className="p-3 space-y-3">
                        {campaignItems.slice(0, 5).map((c) => (
                            <div
                                key={c.id}
                                onClick={() => setSelectedCampaign(c)}
                                className="group cursor-pointer rounded-xl border border-gray-100 bg-white p-4 transition-all hover:shadow-md hover:border-indigo-200 hover:bg-indigo-50/30"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 group-hover:bg-indigo-100 transition-colors">
                                                <Megaphone className="h-4 w-4 text-indigo-500" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold text-gray-900 truncate">{c.name}</p>
                                                <div className="flex items-center gap-1.5 mt-0.5">
                                                    <StatusBadge status={c.status} />
                                                    <TypeBadge type={c.type} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {c.revenue > 0 && (
                                        <div className="text-right shrink-0">
                                            <p className="text-sm font-bold text-indigo-600">{fmt$(c.revenue)}</p>
                                            <p className="text-[10px] text-gray-400">receita</p>
                                        </div>
                                    )}
                                </div>
                                <div className="mt-3 grid grid-cols-4 gap-2">
                                    <div className="rounded-lg bg-gray-50 px-2.5 py-1.5 text-center">
                                        <p className="text-xs font-bold text-gray-900">{fmtN(c.total_sent)}</p>
                                        <p className="text-[9px] text-gray-400">Envios</p>
                                    </div>
                                    <div className="rounded-lg bg-gray-50 px-2.5 py-1.5 text-center">
                                        <p className="text-xs font-bold text-gray-900">{fmtN(c.total_delivered)}</p>
                                        <p className="text-[9px] text-gray-400">Entregues</p>
                                    </div>
                                    <div className="rounded-lg bg-pink-50 px-2.5 py-1.5 text-center">
                                        <p className="text-xs font-bold text-pink-600">{fmtN(c.total_clicks)}</p>
                                        <p className="text-[9px] text-gray-400">Clicks</p>
                                    </div>
                                    <div className="rounded-lg bg-gray-50 px-2.5 py-1.5 text-center">
                                        <CtrBadge ctr={c.ctr} />
                                        <p className="text-[9px] text-gray-400 mt-0.5">CTR</p>
                                    </div>
                                </div>
                                <div className="mt-2 flex items-center justify-between">
                                    <span className="text-[10px] text-gray-400">{fmtDate(c.sent_at)}</span>
                                    <span className="text-[10px] text-indigo-500 font-medium opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                                        <Eye className="h-3 w-3" /> Ver detalhes
                                    </span>
                                </div>
                            </div>
                        ))}
                        {campaignItems.length === 0 && <EmptyState text="Nenhuma campanha enviada" />}
                    </div>
                </Section>

                {/* Active flows */}
                <Section>
                    <SectionHeader title="Flows Ativos" right={
                        <span className="text-xs text-gray-400">{flowItems.length} flows</span>
                    } />
                    <div className="p-3 space-y-3">
                        {flowItems.slice(0, 5).map((f) => (
                            <div
                                key={f.id}
                                onClick={() => setSelectedFlow(f)}
                                className="group cursor-pointer rounded-xl border border-gray-100 bg-white p-4 transition-all hover:shadow-md hover:border-purple-200 hover:bg-purple-50/30"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-50 group-hover:bg-purple-100 transition-colors">
                                                <Zap className="h-4 w-4 text-purple-500" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold text-gray-900 truncate">{f.name}</p>
                                                <div className="flex items-center gap-1.5 mt-0.5">
                                                    <StatusBadge status={f.status} />
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-0.5 text-[10px] font-medium text-purple-600 ring-1 ring-inset ring-purple-500/10">
                                                        {f.trigger_type === 'new_lead' ? 'Novo Lead' : `Tag: ${f.trigger_tag || '-'}`}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    {f.revenue > 0 && (
                                        <div className="text-right shrink-0">
                                            <p className="text-sm font-bold text-purple-600">{fmt$(f.revenue)}</p>
                                            <p className="text-[10px] text-gray-400">receita</p>
                                        </div>
                                    )}
                                </div>
                                {/* Progress bar */}
                                <div className="mt-3">
                                    <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
                                        <span>{fmtN(f.total_entered)} leads</span>
                                        <span>{f.completion_rate}% completaram</span>
                                    </div>
                                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
                                        <div
                                            className="h-full rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 transition-all"
                                            style={{ width: `${Math.min(f.completion_rate, 100)}%` }}
                                        />
                                    </div>
                                </div>
                                <div className="mt-2.5 grid grid-cols-3 gap-2">
                                    <div className="rounded-lg bg-gray-50 px-2.5 py-1.5 text-center">
                                        <p className="text-xs font-bold text-gray-900">{fmtN(f.total_entered)}</p>
                                        <p className="text-[9px] text-gray-400">Entraram</p>
                                    </div>
                                    <div className="rounded-lg bg-pink-50 px-2.5 py-1.5 text-center">
                                        <p className="text-xs font-bold text-pink-600">{fmtN(f.total_clicks)}</p>
                                        <p className="text-[9px] text-gray-400">Clicks</p>
                                    </div>
                                    <div className="rounded-lg bg-gray-50 px-2.5 py-1.5 text-center">
                                        <p className="text-xs font-bold text-purple-600">{f.total_nodes}</p>
                                        <p className="text-[9px] text-gray-400">Notificações</p>
                                    </div>
                                </div>
                                <div className="mt-2 flex items-center justify-between">
                                    <span className="text-[10px] text-gray-400">{fmtN(f.total_completed)} completaram</span>
                                    <span className="text-[10px] text-purple-500 font-medium opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                                        <Eye className="h-3 w-3" /> Ver detalhes
                                    </span>
                                </div>
                            </div>
                        ))}
                        {flowItems.length === 0 && <EmptyState text="Nenhum flow cadastrado" />}
                    </div>
                </Section>
            </div>

            {/* Campaign Detail Modal */}
            <Dialog open={!!selectedCampaign} onOpenChange={(open) => { if (!open) setSelectedCampaign(null); }}>
                <DialogContent className="sm:max-w-lg p-0 overflow-hidden rounded-2xl gap-0">
                    {selectedCampaign && (() => {
                        const c = selectedCampaign;
                        const statusLabels: Record<string, string> = { sent: 'Enviada', draft: 'Rascunho', scheduled: 'Agendada', sending: 'Enviando', cancelled: 'Cancelada', failed: 'Falhou', active: 'Ativo', paused: 'Pausado' };
                        const typeLabels: Record<string, string> = { direct: 'Direta', scheduled: 'Agendada', recurring: 'Recorrente' };
                        const typeIcons: Record<string, any> = { direct: Send, scheduled: Clock, recurring: Repeat };
                        const TypeIcon = typeIcons[c.type] || Send;
                        return (
                            <div className="flex flex-col max-h-[85vh]">
                                {/* Header */}
                                <div className="relative bg-gradient-to-br from-indigo-500 to-blue-600 p-6 text-white">
                                    <div className="absolute -right-6 -top-6 opacity-10">
                                        <Megaphone className="h-28 w-28" strokeWidth={1} />
                                    </div>
                                    <DialogTitle className="text-lg font-bold text-white">{c.name}</DialogTitle>
                                    <div className="mt-2 flex items-center gap-2">
                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-2.5 py-1 text-xs font-medium text-white backdrop-blur-sm">
                                            <span className="h-1.5 w-1.5 rounded-full bg-white" />
                                            {statusLabels[c.status] || c.status}
                                        </span>
                                        <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-xs font-medium text-white/90 backdrop-blur-sm">
                                            <TypeIcon className="h-3 w-3" />
                                            {typeLabels[c.type] || c.type}
                                        </span>
                                    </div>
                                    {c.revenue > 0 && (
                                        <p className="mt-3 text-3xl font-bold">{fmt$(c.revenue)}</p>
                                    )}
                                </div>

                                {/* Scrollable content */}
                                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                                    {/* Push Preview */}
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Notificação</p>
                                        <PushPreview title={c.title} body={c.body} icon={c.icon} url={c.url} />
                                    </div>

                                    {/* Key Metrics Grid */}
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Métricas de Envio</p>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3 text-center">
                                                <Send className="h-4 w-4 text-blue-500 mx-auto" />
                                                <p className="mt-1.5 text-xl font-bold text-gray-900">{fmtN(c.total_sent)}</p>
                                                <p className="text-[10px] text-gray-400">Enviados</p>
                                            </div>
                                            <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3 text-center">
                                                <Bell className="h-4 w-4 text-emerald-500 mx-auto" />
                                                <p className="mt-1.5 text-xl font-bold text-gray-900">{fmtN(c.total_delivered)}</p>
                                                <p className="text-[10px] text-gray-400">Entregues</p>
                                            </div>
                                            <div className="rounded-xl border border-pink-100 bg-pink-50/70 p-3 text-center">
                                                <MousePointerClick className="h-4 w-4 text-pink-500 mx-auto" />
                                                <p className="mt-1.5 text-xl font-bold text-pink-600">{fmtN(c.total_clicks)}</p>
                                                <p className="text-[10px] text-gray-400">Clicks no Push</p>
                                            </div>
                                            <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3 text-center">
                                                <TrendingUp className="h-4 w-4 text-purple-500 mx-auto" />
                                                <p className="mt-1.5 text-xl font-bold text-purple-600">{c.ctr.toFixed(1)}%</p>
                                                <p className="text-[10px] text-gray-400">CTR</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Revenue Metrics */}
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Receita (GAM)</p>
                                        <div className="grid grid-cols-3 gap-3">
                                            <div className="rounded-xl border border-amber-100 bg-amber-50/70 p-3 text-center">
                                                <DollarSign className="h-4 w-4 text-amber-500 mx-auto" />
                                                <p className="mt-1.5 text-lg font-bold text-amber-700">{fmt$(c.revenue)}</p>
                                                <p className="text-[10px] text-gray-400">Receita</p>
                                            </div>
                                            <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3 text-center">
                                                <Eye className="h-4 w-4 text-gray-500 mx-auto" />
                                                <p className="mt-1.5 text-lg font-bold text-gray-900">{fmtN(c.impressions)}</p>
                                                <p className="text-[10px] text-gray-400">Impressões GAM</p>
                                            </div>
                                            <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3 text-center">
                                                <BarChart3 className="h-4 w-4 text-indigo-500 mx-auto" />
                                                <p className="mt-1.5 text-lg font-bold text-indigo-600">{fmt$(c.ecpm)}</p>
                                                <p className="text-[10px] text-gray-400">eCPM</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Failed sends */}
                                    {c.total_failed > 0 && (
                                        <div className="rounded-xl border border-red-100 bg-red-50 p-3 flex items-center gap-3">
                                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-100">
                                                <X className="h-4 w-4 text-red-500" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold text-red-700">{fmtN(c.total_failed)} falhas</p>
                                                <p className="text-xs text-red-500">Envios que falharam</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Details */}
                                    <div className="space-y-2 border-t border-gray-100 pt-4">
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Detalhes</p>
                                        {[
                                            { label: 'Enviada em', value: fmtDate(c.sent_at) },
                                            c.utm_campaign ? { label: 'UTM Campaign', value: c.utm_campaign } : null,
                                            c.url ? { label: 'URL', value: c.url } : null,
                                        ].filter(Boolean).map((item: any) => (
                                            <div key={item.label} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
                                                <span className="text-xs text-gray-400">{item.label}</span>
                                                <span className="text-xs font-medium text-gray-700 max-w-[250px] truncate">{item.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                </DialogContent>
            </Dialog>

            {/* Flow Detail Modal */}
            <Dialog open={!!selectedFlow} onOpenChange={(open) => { if (!open) setSelectedFlow(null); }}>
                <DialogContent className="sm:max-w-lg p-0 overflow-hidden rounded-2xl gap-0">
                    {selectedFlow && (() => {
                        const f = selectedFlow;
                        const flowStatusLabels: Record<string, string> = { active: 'Ativo', paused: 'Pausado', draft: 'Rascunho' };
                        return (
                            <div className="flex flex-col max-h-[85vh]">
                                {/* Header */}
                                <div className="relative bg-gradient-to-br from-purple-500 to-indigo-600 p-6 text-white">
                                    <div className="absolute -right-6 -top-6 opacity-10">
                                        <Zap className="h-28 w-28" strokeWidth={1} />
                                    </div>
                                    <DialogTitle className="text-lg font-bold text-white">{f.name}</DialogTitle>
                                    <div className="mt-2 flex items-center gap-2">
                                        <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-1 text-xs font-medium text-white backdrop-blur-sm">
                                            {f.trigger_type === 'new_lead' ? 'Novo Lead' : `Tag: ${f.trigger_tag || '-'}`}
                                        </span>
                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-xs font-medium text-white/90 backdrop-blur-sm">
                                            <span className="h-1.5 w-1.5 rounded-full bg-white" />
                                            {flowStatusLabels[f.status] || f.status}
                                        </span>
                                    </div>
                                    {f.revenue > 0 && (
                                        <p className="mt-3 text-3xl font-bold">{fmt$(f.revenue)}</p>
                                    )}
                                </div>

                                {/* Scrollable content */}
                                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                                    {/* First notification preview */}
                                    {f.first_title && (
                                        <div>
                                            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-2">1a Notificação</p>
                                            <PushPreview title={f.first_title} body={f.first_body} icon={f.first_icon} />
                                        </div>
                                    )}

                                    {/* Flow Progress */}
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Progresso do Flow</p>
                                        <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                                            <span>{fmtN(f.total_entered)} leads</span>
                                            <span>{f.completion_rate}% completaram</span>
                                        </div>
                                        <div className="h-3 w-full overflow-hidden rounded-full bg-gray-100">
                                            <div
                                                className="h-full rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 transition-all"
                                                style={{ width: `${Math.min(f.completion_rate, 100)}%` }}
                                            />
                                        </div>
                                    </div>

                                    {/* Key Metrics */}
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Métricas</p>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3 text-center">
                                                <Users className="h-4 w-4 text-blue-500 mx-auto" />
                                                <p className="mt-1.5 text-xl font-bold text-gray-900">{fmtN(f.total_entered)}</p>
                                                <p className="text-[10px] text-gray-400">Entraram</p>
                                            </div>
                                            <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3 text-center">
                                                <Users className="h-4 w-4 text-emerald-500 mx-auto" />
                                                <p className="mt-1.5 text-xl font-bold text-gray-900">{fmtN(f.total_completed)}</p>
                                                <p className="text-[10px] text-gray-400">Completaram</p>
                                            </div>
                                            <div className="rounded-xl border border-pink-100 bg-pink-50/70 p-3 text-center">
                                                <MousePointerClick className="h-4 w-4 text-pink-500 mx-auto" />
                                                <p className="mt-1.5 text-xl font-bold text-pink-600">{fmtN(f.total_clicks)}</p>
                                                <p className="text-[10px] text-gray-400">Clicks no Push</p>
                                            </div>
                                            <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3 text-center">
                                                <Bell className="h-4 w-4 text-purple-500 mx-auto" />
                                                <p className="mt-1.5 text-xl font-bold text-purple-600">{f.total_nodes}</p>
                                                <p className="text-[10px] text-gray-400">Notificações</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Revenue Metrics */}
                                    <div>
                                        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Receita (GAM)</p>
                                        <div className="grid grid-cols-3 gap-3">
                                            <div className="rounded-xl border border-amber-100 bg-amber-50/70 p-3 text-center">
                                                <DollarSign className="h-4 w-4 text-amber-500 mx-auto" />
                                                <p className="mt-1.5 text-lg font-bold text-amber-700">{fmt$(f.revenue)}</p>
                                                <p className="text-[10px] text-gray-400">Receita</p>
                                            </div>
                                            <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3 text-center">
                                                <Eye className="h-4 w-4 text-gray-500 mx-auto" />
                                                <p className="mt-1.5 text-lg font-bold text-gray-900">{fmtN(f.impressions)}</p>
                                                <p className="text-[10px] text-gray-400">Impressões GAM</p>
                                            </div>
                                            <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3 text-center">
                                                <BarChart3 className="h-4 w-4 text-indigo-500 mx-auto" />
                                                <p className="mt-1.5 text-lg font-bold text-indigo-600">{fmt$(f.ecpm)}</p>
                                                <p className="text-[10px] text-gray-400">eCPM</p>
                                            </div>
                                        </div>
                                    </div>

                                    {f.description && (
                                        <div className="border-t border-gray-100 pt-4">
                                            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Descrição</p>
                                            <p className="text-sm text-gray-600">{f.description}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })()}
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ════════════════════════════════════════════
//  CAMPAIGNS TAB
// ════════════════════════════════════════════

function CampaignsTab({ report }: { report?: DashboardCampaignsReport }) {
    const summary = report?.summary;
    const items = report?.items || [];

    return (
        <div className="space-y-6">
            {/* Summary metrics */}
            {summary && (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                    <MetricCard label="Campanhas" value={summary.total_campaigns} color="indigo" />
                    <MetricCard label="Enviados" value={fmtN(summary.total_sent)} color="blue" sub={`${fmtN(summary.total_delivered)} entregues`} />
                    <MetricCard label="Clicks" value={fmtN(summary.total_clicks)} color="pink" sub={`CTR ${summary.ctr}%`} />
                    <MetricCard label="Receita" value={fmt$(summary.total_revenue)} color="amber" sub={`eCPM ${fmt$(summary.ecpm)}`} />
                    <MetricCard label="Impressões" value={fmtN(summary.total_impressions)} color="emerald" />
                </div>
            )}

            {/* Campaign cards grid */}
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {items.map((c) => (
                    <CampaignDashboardCard key={c.id} campaign={c} />
                ))}
            </div>
            {items.length === 0 && (
                <Section>
                    <EmptyState text="Nenhuma campanha enviada no período" />
                </Section>
            )}
        </div>
    );
}

// ─── Campaign Card for Dashboard ───

function CampaignDashboardCard({ campaign: c }: { campaign: DashboardCampaignItem }) {
    return (
        <div className="group overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-all hover:shadow-lg hover:shadow-gray-200/50">
            {/* Header */}
            <div className="p-5 pb-0">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-gray-900 line-clamp-1">{c.name}</p>
                        <div className="mt-1.5 flex items-center gap-2">
                            <StatusBadge status={c.status} />
                            <TypeBadge type={c.type} />
                        </div>
                    </div>
                    {c.revenue > 0 && (
                        <div className="shrink-0 text-right">
                            <p className="text-lg font-bold text-indigo-600">{fmt$(c.revenue)}</p>
                            <p className="text-[10px] text-gray-400">receita</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Push Preview */}
            <div className="px-5 pt-4">
                <PushPreview title={c.title} body={c.body} icon={c.icon} url={c.url} />
            </div>

            {/* Metrics */}
            <div className="mt-4 grid grid-cols-4 gap-px bg-gray-100">
                <div className="bg-white p-3 text-center">
                    <p className="text-sm font-bold text-gray-900">{fmtN(c.total_sent)}</p>
                    <p className="text-[10px] text-gray-400">Enviados</p>
                </div>
                <div className="bg-white p-3 text-center">
                    <p className="text-sm font-bold text-gray-900">{fmtN(c.total_delivered)}</p>
                    <p className="text-[10px] text-gray-400">Entregues</p>
                </div>
                <div className="bg-white p-3 text-center">
                    <p className="text-sm font-bold text-pink-600">{fmtN(c.total_clicks)}</p>
                    <p className="text-[10px] text-gray-400">Clicks</p>
                </div>
                <div className="bg-white p-3 text-center">
                    <CtrBadge ctr={c.ctr} />
                    <p className="mt-0.5 text-[10px] text-gray-400">CTR</p>
                </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-5 py-3 text-[11px] text-gray-400">
                <span>{fmtDate(c.sent_at)}</span>
                <div className="flex items-center gap-3">
                    {c.impressions > 0 && <span>{fmtN(c.impressions)} imp</span>}
                    {c.ecpm > 0 && <span>eCPM {fmt$(c.ecpm)}</span>}
                </div>
            </div>
        </div>
    );
}

// ════════════════════════════════════════════
//  FLOWS TAB
// ════════════════════════════════════════════

function FlowsTab({ report }: { report?: DashboardFlowsReport }) {
    const summary = report?.summary;
    const items = report?.items || [];

    return (
        <div className="space-y-6">
            {/* Summary metrics */}
            {summary && (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                    <MetricCard label="Flows" value={summary.total_flows} color="purple" />
                    <MetricCard label="Leads Entraram" value={fmtN(summary.total_entered)} color="blue" sub={`${summary.completion_rate}% completaram`} />
                    <MetricCard label="Clicks" value={fmtN(summary.total_clicks)} color="pink" />
                    <MetricCard label="Receita" value={fmt$(summary.total_revenue)} color="amber" sub={`eCPM ${fmt$(summary.ecpm)}`} />
                    <MetricCard label="Impressões" value={fmtN(summary.total_impressions)} color="emerald" />
                </div>
            )}

            {/* Flow cards grid */}
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {items.map((f) => (
                    <FlowDashboardCard key={f.id} flow={f} />
                ))}
            </div>
            {items.length === 0 && (
                <Section>
                    <EmptyState text="Nenhum flow cadastrado" />
                </Section>
            )}
        </div>
    );
}

// ─── Flow Card for Dashboard ───

function FlowDashboardCard({ flow: f }: { flow: DashboardFlowItem }) {
    const triggerLabel = f.trigger_type === 'new_lead' ? 'Novo Lead' : `Tag: ${f.trigger_tag || '-'}`;

    return (
        <div className="group overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-all hover:shadow-lg hover:shadow-gray-200/50">
            {/* Header */}
            <div className="p-5 pb-0">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-gray-900 line-clamp-1">{f.name}</p>
                        <div className="mt-1.5 flex items-center gap-2">
                            <StatusBadge status={f.status} />
                            <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-600 ring-1 ring-inset ring-purple-500/10">
                                <Zap className="h-3 w-3" />
                                {triggerLabel}
                            </span>
                        </div>
                        {f.description && (
                            <p className="mt-1 text-xs text-gray-400 line-clamp-1">{f.description}</p>
                        )}
                    </div>
                    {f.revenue > 0 && (
                        <div className="shrink-0 text-right">
                            <p className="text-lg font-bold text-indigo-600">{fmt$(f.revenue)}</p>
                            <p className="text-[10px] text-gray-400">receita</p>
                        </div>
                    )}
                </div>
            </div>

            {/* First notification preview */}
            {f.first_title && (
                <div className="px-5 pt-4">
                    <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-gray-300">
                        1ª notificação
                    </p>
                    <PushPreview title={f.first_title} body={f.first_body} icon={f.first_icon} />
                </div>
            )}

            {/* Flow progress */}
            <div className="mx-5 mt-4">
                <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span>{fmtN(f.total_entered)} leads</span>
                    <span>{f.completion_rate}% completaram</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                    <div
                        className="h-full rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 transition-all"
                        style={{ width: `${Math.min(f.completion_rate, 100)}%` }}
                    />
                </div>
            </div>

            {/* Metrics */}
            <div className="mt-4 grid grid-cols-3 gap-px bg-gray-100">
                <div className="bg-white p-3 text-center">
                    <p className="text-sm font-bold text-gray-900">{fmtN(f.total_entered)}</p>
                    <p className="text-[10px] text-gray-400">Entraram</p>
                </div>
                <div className="bg-white p-3 text-center">
                    <p className="text-sm font-bold text-pink-600">{fmtN(f.total_clicks)}</p>
                    <p className="text-[10px] text-gray-400">Clicks</p>
                </div>
                <div className="bg-white p-3 text-center">
                    <p className="text-sm font-bold text-purple-600">{f.total_nodes}</p>
                    <p className="text-[10px] text-gray-400">Notificações</p>
                </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-5 py-3 text-[11px] text-gray-400">
                <span>{f.total_completed} completaram</span>
                <div className="flex items-center gap-3">
                    {f.impressions > 0 && <span>{fmtN(f.impressions)} imp</span>}
                    {f.ecpm > 0 && <span>eCPM {fmt$(f.ecpm)}</span>}
                </div>
            </div>
        </div>
    );
}
